import React from 'react';

import { T /* defineMessages */ } from '@sm/intl';
import { CaretDownOutlineIcon } from '@sm/question-ui/respondent-survey';
import { LanguageTag } from '~lib/generatedGqlTypes';
import { NativeSelect as Select, NativeSelectOption as Option } from '~app/components/core/NativeSelect';

import useStyles from './useStyles';

const COPY = /* defineMessages */ {
  SELECT_LABEL: {
    id: 'SurveyTaking.LanguageSelect',
    defaultMessage: 'Change survey language',
    description: `[Type: label][Vis: high] - the text that describes the language select`,
  },
};

export type LanguageSelectProps = {
  /** The current selected language option (use `LanguageTag.code`) */
  selectedLangCode: string;
  /** The languages representing options within the select element */
  languages: Pick<LanguageTag, 'id' | 'name' | 'displayName' | 'code'>[];
  /** The change event fired when the select value is changed */
  onChange?: React.SelectHTMLAttributes<HTMLSelectElement>['onChange'];
};

export function LanguageSelect({
  selectedLangCode,
  languages,
  onChange,
}: LanguageSelectProps): React.ReactElement | null {
  const { selectContainer, srOnly } = useStyles();

  // if there is just one language then the survey only supports
  // the currently selected language and we don't show the select
  if (languages.length <= 1) {
    return null;
  }

  const handleLanguageSelect = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    onChange?.(event);
    if (!event.isDefaultPrevented()) {
      window.location.assign(`?lang=${event.target.value}`);
    }
  };

  return (
    <>
      <label className={srOnly} htmlFor="survey-language">
        <T desc={COPY.SELECT_LABEL} />
      </label>
      <Select
        id="survey-language"
        icon={<CaretDownOutlineIcon />}
        onChange={handleLanguageSelect}
        value={selectedLangCode}
        className={selectContainer}
        data-testid="LanguageSelect"
      >
        {languages.map(lang => (
          <Option key={lang.id} id={lang.id} lang={lang.code.split('_')[0]} value={lang.code}>
            {lang.displayName}
          </Option>
        ))}
      </Select>
    </>
  );
}

export default LanguageSelect;
